import React from 'react';
import styled from '@emotion/native';
import { TouchableOpacity } from 'react-native';
import Modal from '@/shared/components/Modal';
import Button from '@/shared/components/Button';
import { BodyText, H1 } from '@/shared/components/StyledText';
import ReceiptItem from '@/shared/components/Receipt/ReceiptItem';
import PaymentMethodDisplay from '@/shared/components/Receipt/PaymentMethodDisplay';
import { ReceiptItemProps } from '@/shared/components/Receipt/ReceiptItem';
import { ReceiptLabelText } from '@/shared/components/Receipt/constants';
import {
  ReasonIcon,
  StylistIcon,
} from '@/shared/components/Receipt/ReceiptIcons';
import DashedBorder from '@/shared/components/Receipt/DashedBorder';
import { green, white, lightGreen, sand } from '@/shared/styles/colors';
import { formatCurrency } from '@/shared/utils';
import { BOX_SHADOW_STYLE } from '@/shared/styles/constants';

interface LookbookPurchaseModalProps {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  price: number; // Price in cents
  stylistName: string;
  onCheckout: () => void;
  onHelpCenterPress?: () => void;
  isProcessing?: boolean;
}

const LookbookPurchaseModal: React.FC<LookbookPurchaseModalProps> = ({
  isVisible,
  setIsVisible,
  price,
  stylistName,
  onCheckout,
  onHelpCenterPress,
  isProcessing = false,
}) => {
  const formattedPrice = formatCurrency(price);

  const handleBackPress = () => {
    setIsVisible(false);
  };

  const handleHelpCenterPress = () => {
    onHelpCenterPress?.();
  };

  // Create receipt items array (excluding Price and Client Notes)
  const receiptItems: ReceiptItemProps[] = [
    {
      label: ReceiptLabelText.Product,
      value: 'Lookbook',
      icon: <ReasonIcon />,
    },
    {
      label: ReceiptLabelText.Stylist,
      value: stylistName,
      icon: <StylistIcon />,
    },
    {
      label: ReceiptLabelText.PaymentMethod,
      customElement: <PaymentMethodDisplay />,
    },
  ];

  return (
    <Modal
      isVisible={isVisible}
      setIsVisible={setIsVisible}
      mobileBackgroundColor={sand}
    >
      <Container>
        <TopSection>
          <BackButton onPress={handleBackPress}>
            <BackIcon source={require('@/assets/images/BackWithCircle.png')} />
          </BackButton>
          <HeaderContainer>
            <Title>{`PURCHASE\nSUMMARY`}</Title>
          </HeaderContainer>

          <CheckoutButtonContainer>
            <Button
              text="CHECKOUT"
              onPress={onCheckout}
              disabled={isProcessing}
              loading={isProcessing}
              customStyle={{
                width: '100%',
                height: 42,
                borderRadius: 21,
              }}
              customLabelStyle={{
                fontSize: 13,
                fontWeight: '700',
                letterSpacing: -0.13,
              }}
            />
          </CheckoutButtonContainer>
        </TopSection>

        <WhiteSection>
          <TotalPriceSection>
            <TotalPriceLabel>Total Price:</TotalPriceLabel>
            <TotalPriceValue>{formattedPrice}</TotalPriceValue>
          </TotalPriceSection>

          {/* Custom Receipt without Client Notes */}
          <CustomReceiptContainer>
            <DashedBorder />
            {receiptItems.map((item) => (
              <ReceiptItem key={item.label} {...item} />
            ))}
            <DashedBorder />
          </CustomReceiptContainer>
        </WhiteSection>
        <HelpCenterContainer>
          <HelpText>Have Questions? </HelpText>
          <TouchableOpacity onPress={handleHelpCenterPress}>
            <HelpLinkText>Visit Help Center</HelpLinkText>
          </TouchableOpacity>
        </HelpCenterContainer>
      </Container>
    </Modal>
  );
};

const Container = styled.View({
  width: '100%',
  height: '100%',
  borderRadius: 0,
  backgroundColor: 'transparent',
  overflow: 'hidden',
});

const TopSection = styled.View({
  backgroundColor: sand,
  paddingHorizontal: 32,
  paddingTop: 60,
  paddingBottom: 32,
});

const WhiteSection = styled.View({
  backgroundColor: white,
  flex: 1,
});

const HeaderContainer = styled.View({
  position: 'relative',
  alignItems: 'center',
  marginTop: 24,
  marginBottom: 24,
});

const BackButton = styled.TouchableOpacity({
  position: 'absolute',
  left: 32,
  top: 60,
  zIndex: 1,
});

const BackIcon = styled.Image({
  width: 34,
  height: 34,
});

const Title = styled(H1)({
  fontSize: 30,
  fontFamily: 'Lora',
  fontWeight: '400',
  letterSpacing: -0.3,
  lineHeight: 33,
  color: green,
  textAlign: 'center',
});

const CheckoutButtonContainer = styled.View({
  width: '100%',
});

const TotalPriceSection = styled.View({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingVertical: 24,
  paddingHorizontal: 24,
});

const TotalPriceLabel = styled(BodyText)({
  fontSize: 14,
  fontFamily: 'Helvetica',
  fontWeight: '700',
  letterSpacing: -0.14,
  color: green,
});

const TotalPriceValue = styled(H1)({
  fontSize: 30,
  fontFamily: 'Lora',
  fontWeight: '400',
  letterSpacing: -0.3,
  lineHeight: 33,
  color: green,
});

const CustomReceiptContainer = styled.View({
  backgroundColor: white,
  borderRadius: 8,
  marginBottom: 24,
  ...BOX_SHADOW_STYLE,
});

const HelpCenterContainer = styled.View({
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: white,
  paddingBottom: 64,
});

const HelpText = styled(BodyText)({
  fontSize: 14,
  fontFamily: 'HelveticaNeue',
  fontWeight: '400',
  letterSpacing: -0.14,
  color: lightGreen,
});

const HelpLinkText = styled(BodyText)({
  fontSize: 14,
  fontFamily: 'HelveticaNeue',
  fontWeight: '500',
  textDecorationLine: 'underline',
  letterSpacing: -0.14,
  color: green,
});

export default LookbookPurchaseModal;
